/**
 * Performance Optimizer for Cross-Browser Animation Compatibility
 * Automatically adjusts animation complexity and settings based on browser capabilities
 */

import { browserCompatibility, getBrowserInfo, getAnimationCapabilities } from './browserCompatibility';

export interface OptimizationSettings {
  enableComplexAnimations: boolean;
  maxConcurrentAnimations: number;
  preferredFrameRate: number;
  useGPUAcceleration: boolean;
  enableParallax: boolean;
  enable3DTransforms: boolean;
  enableBackdropFilter: boolean;
  animationDurationMultiplier: number;
}

export interface PerformanceMetrics {
  averageFPS: number;
  frameDrops: number;
  memoryUsage: number;
  cpuUsage: number;
  batteryLevel?: number;
  isLowPowerMode?: boolean;
}

class AnimationPerformanceOptimizer {
  private settings: OptimizationSettings;
  private metrics: PerformanceMetrics;
  private activeAnimations: Set<string> = new Set();
  private performanceObserver: PerformanceObserver | null = null;
  private frameRateMonitor: number | null = null;
  private lastFrameTime: number = 0;
  private frameCount: number = 0;
  private frameTimes: number[] = [];
  private lastWarningTime: number = 0;

  constructor() {
    this.settings = this.generateOptimalSettings();
    this.metrics = this.initializeMetrics();
    this.startPerformanceMonitoring();
    this.adaptToSystemChanges();
  }

  private generateOptimalSettings(): OptimizationSettings {
    const browserInfo = getBrowserInfo();
    const capabilities = getAnimationCapabilities();
    const performanceProfile = browserCompatibility.getPerformanceProfile();

    const baseSettings: OptimizationSettings = {
      enableComplexAnimations: true,
      maxConcurrentAnimations: capabilities.maxConcurrentAnimations,
      preferredFrameRate: capabilities.recommendedFrameRate,
      useGPUAcceleration: capabilities.shouldUseGPUAcceleration,
      enableParallax: true,
      enable3DTransforms: capabilities.supportsComplexTransforms,
      enableBackdropFilter: capabilities.supportsBackdropFilter,
      animationDurationMultiplier: 1.0
    };

    // Adjust based on performance profile
    if (performanceProfile === 'low') {
      baseSettings.enableComplexAnimations = false;
      baseSettings.maxConcurrentAnimations = Math.min(4, baseSettings.maxConcurrentAnimations);
      baseSettings.preferredFrameRate = 30;
      baseSettings.enableParallax = false;
      baseSettings.enable3DTransforms = false;
      baseSettings.enableBackdropFilter = false;
      baseSettings.animationDurationMultiplier = 1.5; // Slower animations
    } else if (performanceProfile === 'medium') {
      baseSettings.enableComplexAnimations = true;
      baseSettings.maxConcurrentAnimations = Math.min(6, baseSettings.maxConcurrentAnimations);
      baseSettings.enableParallax = true;
      baseSettings.animationDurationMultiplier = 1.2;
    }

    // Browser-specific optimizations
    if (browserInfo.name === 'safari') {
      baseSettings.animationDurationMultiplier *= 1.1; // Slightly slower for Safari
      if (/iPhone|iPad/.test(navigator.userAgent)) {
        baseSettings.maxConcurrentAnimations = Math.min(3, baseSettings.maxConcurrentAnimations);
        baseSettings.enableParallax = false;
      }
    }

    if (browserInfo.name === 'firefox') {
      baseSettings.enableBackdropFilter = browserInfo.version >= 103;
      baseSettings.animationDurationMultiplier *= 0.9; // Slightly faster for Firefox
    }

    // Respect user preferences
    if (browserInfo.prefersReducedMotion) {
      baseSettings.enableComplexAnimations = false;
      baseSettings.enableParallax = false;
      baseSettings.enable3DTransforms = false;
      baseSettings.animationDurationMultiplier = 0.5; // Much faster or instant
    }

    return baseSettings;
  }

  private initializeMetrics(): PerformanceMetrics {
    return {
      averageFPS: 60,
      frameDrops: 0,
      memoryUsage: 0,
      cpuUsage: 0
    };
  }

  private startPerformanceMonitoring(): void {
    // Monitor frame rate
    this.startFrameRateMonitoring();

    // Monitor performance entries
    if ('PerformanceObserver' in window) {
      this.performanceObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        this.processPerformanceEntries(entries);
      });

      try {
        this.performanceObserver.observe({ 
          entryTypes: ['measure', 'navigation', 'paint', 'layout-shift'] 
        });
      } catch (e) {
        console.warn('Some performance metrics not available:', e);
      }
    }

    // Monitor memory usage (if available)
    this.startMemoryMonitoring();
  }

  private startFrameRateMonitoring(): void {
    const measureFrame = (timestamp: number) => {
      if (this.lastFrameTime > 0) {
        const deltaTime = timestamp - this.lastFrameTime;
        this.frameTimes.push(deltaTime);
        
        // Keep only last 60 frames for rolling average
        if (this.frameTimes.length > 60) {
          this.frameTimes.shift();
        }

        // Calculate current FPS
        const averageFrameTime = this.frameTimes.reduce((a, b) => a + b, 0) / this.frameTimes.length;
        this.metrics.averageFPS = 1000 / averageFrameTime;

        // Count frame drops (frames taking longer than 16.67ms for 60fps)
        if (deltaTime > 16.67) {
          this.metrics.frameDrops++;
        }

        // Adapt settings if performance is poor
        this.adaptToPerformance();
      }

      this.lastFrameTime = timestamp;
      this.frameCount++;

      this.frameRateMonitor = requestAnimationFrame(measureFrame);
    };

    this.frameRateMonitor = requestAnimationFrame(measureFrame);
  }

  private processPerformanceEntries(entries: PerformanceEntry[]): void {
    entries.forEach(entry => {
      if (entry.entryType === 'measure' && entry.name.startsWith('animation-')) {
        // Track animation performance
        if (entry.duration > 16.67) {
          console.warn(`Slow animation detected: ${entry.name} took ${entry.duration}ms`);
        }
      }
    });
  }

  private startMemoryMonitoring(): void {
    // Use performance.memory if available (Chrome)
    if ('memory' in performance) {
      setInterval(() => {
        const memory = (performance as any).memory;
        this.metrics.memoryUsage = memory.usedJSHeapSize / memory.jsHeapSizeLimit;
        
        // Reduce animations if memory usage is high
        if (this.metrics.memoryUsage > 0.8) {
          this.reduceAnimationComplexity();
        }
      }, 5000);
    }
  }

  private adaptToPerformance(): void {
    const { averageFPS, frameDrops } = this.metrics;

    // Only log warnings every 5 seconds to avoid spam
    const now = Date.now();
    const shouldLog = !this.lastWarningTime || (now - this.lastWarningTime) > 5000;

    // If FPS drops below 30, reduce animation complexity
    if (averageFPS < 30 && this.settings.enableComplexAnimations) {
      if (shouldLog) {
        console.warn('Low FPS detected, reducing animation complexity');
        this.lastWarningTime = now;
      }
      this.settings.enableComplexAnimations = false;
      this.settings.maxConcurrentAnimations = Math.max(2, this.settings.maxConcurrentAnimations - 2);
      this.settings.enableParallax = false;
    }

    // If too many frame drops, further reduce
    if (frameDrops > this.frameCount * 0.2) {
      if (shouldLog) {
        console.warn('High frame drop rate, further reducing animations');
        this.lastWarningTime = now;
      }
      this.settings.enable3DTransforms = false;
      this.settings.enableBackdropFilter = false;
      this.settings.animationDurationMultiplier *= 1.5;
    }
  }

  private reduceAnimationComplexity(): void {
    const now = Date.now();
    if (!this.lastWarningTime || (now - this.lastWarningTime) > 5000) {
      console.warn('High memory usage detected, reducing animation complexity');
      this.lastWarningTime = now;
    }
    this.settings.enableComplexAnimations = false;
    this.settings.maxConcurrentAnimations = Math.max(1, this.settings.maxConcurrentAnimations - 1);
    this.settings.enableParallax = false;
    this.settings.enable3DTransforms = false;
  }

  private adaptToSystemChanges(): void {
    // Listen for battery status changes
    if ('getBattery' in navigator) {
      (navigator as any).getBattery().then((battery: any) => {
        const updateBatteryStatus = () => {
          this.metrics.batteryLevel = battery.level;
          this.metrics.isLowPowerMode = battery.level < 0.2 || !battery.charging;

          if (this.metrics.isLowPowerMode) {
            this.enablePowerSavingMode();
          }
        };

        battery.addEventListener('levelchange', updateBatteryStatus);
        battery.addEventListener('chargingchange', updateBatteryStatus);
        updateBatteryStatus();
      });
    }

    // Listen for reduced motion preference changes
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    mediaQuery.addEventListener('change', (e) => {
      if (e.matches) {
        this.enableReducedMotionMode();
      } else {
        this.settings = this.generateOptimalSettings();
      }
    });

    // Listen for connection changes
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      const updateConnection = () => {
        if (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g') {
          this.enableLowBandwidthMode();
        }
      };

      connection.addEventListener('change', updateConnection);
      updateConnection();
    }
  }

  private enablePowerSavingMode(): void {
    console.log('Enabling power saving mode for animations');
    this.settings.enableComplexAnimations = false;
    this.settings.maxConcurrentAnimations = 1;
    this.settings.preferredFrameRate = 30;
    this.settings.enableParallax = false;
    this.settings.enable3DTransforms = false;
    this.settings.enableBackdropFilter = false;
    this.settings.animationDurationMultiplier = 2.0;
  }

  private enableReducedMotionMode(): void {
    console.log('Enabling reduced motion mode');
    this.settings.enableComplexAnimations = false;
    this.settings.enableParallax = false;
    this.settings.enable3DTransforms = false;
    this.settings.animationDurationMultiplier = 0.1; // Nearly instant
  }

  private enableLowBandwidthMode(): void {
    console.log('Enabling low bandwidth mode');
    this.settings.enableComplexAnimations = false;
    this.settings.maxConcurrentAnimations = 2;
    this.settings.enableParallax = false;
  }

  // Public API
  public getSettings(): OptimizationSettings {
    return { ...this.settings };
  }

  public getMetrics(): PerformanceMetrics {
    return { ...this.metrics };
  }

  public shouldUseAnimation(complexity: 'low' | 'medium' | 'high'): boolean {
    if (!this.settings.enableComplexAnimations && complexity === 'high') {
      return false;
    }

    if (this.activeAnimations.size >= this.settings.maxConcurrentAnimations) {
      return complexity === 'low';
    }

    return true;
  }

  public registerAnimation(id: string): boolean {
    if (this.activeAnimations.size >= this.settings.maxConcurrentAnimations) {
      return false;
    }

    this.activeAnimations.add(id);
    return true;
  }

  public unregisterAnimation(id: string): void {
    this.activeAnimations.delete(id);
  }

  public getOptimalAnimationConfig(type: 'entrance' | 'hover' | 'scroll' | 'transition') {
    const baseConfig = {
      duration: 300,
      ease: 'easeOut',
      useGPU: this.settings.useGPUAcceleration
    };

    // Apply duration multiplier
    baseConfig.duration *= this.settings.animationDurationMultiplier;

    // Type-specific adjustments with framer-motion compatible easing
    switch (type) {
      case 'entrance':
        baseConfig.duration *= 1.2;
        baseConfig.ease = [0.25, 0.46, 0.45, 0.94];
        break;
      case 'hover':
        baseConfig.duration *= 0.5;
        baseConfig.ease = 'easeOut';
        break;
      case 'scroll':
        baseConfig.duration *= 0.8;
        baseConfig.ease = 'easeInOut';
        break;
      case 'transition':
        baseConfig.duration *= 1.5;
        baseConfig.ease = [0.4, 0, 0.2, 1];
        break;
    }

    return baseConfig;
  }

  public cleanup(): void {
    if (this.frameRateMonitor) {
      cancelAnimationFrame(this.frameRateMonitor);
    }

    if (this.performanceObserver) {
      this.performanceObserver.disconnect();
    }
  }
}

// Export singleton instance
export const performanceOptimizer = new AnimationPerformanceOptimizer();

// Utility functions
export const shouldUseAnimation = (complexity: 'low' | 'medium' | 'high') =>
  performanceOptimizer.shouldUseAnimation(complexity);

export const getOptimalAnimationConfig = (type: 'entrance' | 'hover' | 'scroll' | 'transition') =>
  performanceOptimizer.getOptimalAnimationConfig(type);

export const registerAnimation = (id: string) => performanceOptimizer.registerAnimation(id);
export const unregisterAnimation = (id: string) => performanceOptimizer.unregisterAnimation(id);

// Cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    performanceOptimizer.cleanup();
  });
}